import React, { useEffect, useRef, useState } from 'react';
import { CityModal } from './CityModal'; // Adjust path if needed
import { CityPoint } from '../../mapdata/cityData';
import { Server } from 'lincd-server-utils/lib/utils/Server';
import { packageName } from '../../package';
import { CoreMap } from 'lincd/lib/collections/CoreMap';
import { Capacitor } from '@capacitor/core';
import { useIsMobile } from '../../hooks/useMobile';

type InteractiveGlobeProps = {
  gradientColors?: string[];
  pointColor?: string;
  backgroundColor?: string;
  event?: string;
};

export const InteractiveGlobe: React.FC<InteractiveGlobeProps> = ({
  event,
  gradientColors = [
    '#313695',
    '#4575b4',
    '#74add1',
    '#abd9e9',
    '#e0f3f8',
    '#ffffbf',
    '#fee090',
    '#fdae61',
    '#f46d43',
    '#d73027',
    '#a50026',
  ],
  pointColor = '#FFF000',
  backgroundColor = '#032265',
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef(null);
  const isInitialized = useRef(false);
  const isMobile = useIsMobile();

  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedCityName, setSelectedCityName] = useState<string>('');
  const [cityStats, setCityStats] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [cityPoints, setCityPoints] = useState<CityPoint[]>([]);

  const getCityData = async () => {
    try {
      const response = await Server.call(
        packageName,
        'getCityData',
        selectedCity,
      );
      setCityStats(response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCityPoints = async () => {
    try {
      const cities = await Server.call(packageName, 'getPlayerCities', event);
      const data: CityPoint[] = [];
      cities.forEach((city, key) => {
        data.push({
          name: city.name,
          value: [city.long, city.lat, 0],
          uri: key,
        });
      });
      setCityPoints(data);
    } catch (err) {
      console.error('Failed to fetch player cities:', err);
    }
  };

  useEffect(() => {
    getCityData();
  }, [selectedCity]);

  // retrieve city points on globe and update every minute
  useEffect(() => {
    fetchCityPoints();

    const interval = setInterval(() => {
      fetchCityPoints();
    }, 1000 * 60); // every minute

    return () => clearInterval(interval);
  }, [event]);

  useEffect(() => {
    let resizeObserver, chart;

    const initChart = async () => {
      try {
        // dynamically import echarts modules
        const echarts = await import('echarts/core');
        const { TooltipComponent, VisualMapComponent } = await import(
          'echarts/components'
        );
        const { CanvasRenderer } = await import('echarts/renderers');
        await import('echarts-gl');

        echarts.use([TooltipComponent, VisualMapComponent, CanvasRenderer]);

        // Initialize the chart only once
        if (chartRef.current && !isInitialized.current) {
          chart = echarts.init(chartRef.current, undefined, {
            renderer: 'canvas',
          });
          chartInstanceRef.current = chart;
          isInitialized.current = true;

          const globeOption = {
            backgroundColor,
            tooltip: {
              trigger: 'item',
              formatter: function (params: any) {
                return params.name; // City name only
              },
              textStyle: {
                fontFamily: 'Roboto',
                fontSize: 14,
                color: '#032265',
              },
            },
            globe: {
              baseTexture: Capacitor.isNativePlatform()
                ? 'images/maps/g_base_bright_compressed.jpg'
                : process.env.SITE_ROOT +
                  '/public/images/maps/g_base_bright_compressed.jpg',
              heightTexture: Capacitor.isNativePlatform()
                ? 'images/maps/g_bump_compressed.jpg'
                : process.env.SITE_ROOT +
                  '/public/images/maps/g_bump_compressed.jpg',
              shading: 'lambert',
              environment: backgroundColor,
              light: {
                main: { intensity: 0 },
                ambient: { intensity: 1.2 },
              },
              viewControl: {
                autoRotate: true,
                autoRotateAfterStill: 5,
                distance: 200,
                zoomSensitivity: 0,
                panSensitivity: 1,
                rotateSensitivity: 1,
                alpha: 22,
              },
            },
            series: [
              {
                type: 'scatter3D',
                coordinateSystem: 'globe',
                symbolSize: 10,
                itemStyle: {
                  color: pointColor,
                  opacity: 0.9,
                },
                blendMode: 'lighter',
                data: cityPoints, // use the fetched city points
                label: {
                  show: false, // 👈 this disables the 0 label being rendered
                  formatter: () => '',
                },
                emphasis: {
                  label: {
                    show: false,
                  },
                },
              },
            ],
          };

          chart.setOption(globeOption, true);

          chart.on('click', (params: any) => {
            if (params.seriesType === 'scatter3D' && params.data.uri) {
              setSelectedCity(params.data.uri);
              setSelectedCityName(params.data.name);
              setModalOpen(true);
            }
          });

          // Set up resize observer
          resizeObserver = new ResizeObserver(() => {
            if (chartInstanceRef.current) {
              chartInstanceRef.current.resize();
            }
          });

          if (chartRef.current) {
            resizeObserver.observe(chartRef.current);
          }
        }
      } catch (err) {
        console.error('Error initializing chart:', err);
      }
    };

    initChart();

    // Cleanup function for component unmount only
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
        isInitialized.current = false;
      }
    };
  }, [backgroundColor, pointColor]); // Only re-run if these props change

  // update city points when the data changes
  useEffect(() => {
    if (chartInstanceRef.current && cityPoints.length > 0) {
      chartInstanceRef.current.setOption({
        series: [
          {
            type: 'scatter3D',
            coordinateSystem: 'globe',
            symbolSize: 10,
            itemStyle: {
              color: pointColor,
              opacity: 0.9,
            },
            blendMode: 'lighter',
            data: cityPoints,
            label: {
              show: false,
              formatter: () => '',
            },
            emphasis: {
              label: {
                show: false,
              },
            },
          },
        ],
      });
    }
  }, [cityPoints]);

  // Force a resize when the modal changes state to ensure proper rendering
  useEffect(() => {
    if (chartInstanceRef.current) {
      setTimeout(() => {
        chartInstanceRef.current?.resize();
      }, 200);
    }
  }, [modalOpen]);

  return (
    <div
      style={{
        position: 'relative',
        // width: '50%',
        //  paddingBottom: '50%'
      }}
    >
      {/* <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        }}
      > */}
      <div
        ref={chartRef}
        style={{
          width: '500px',
          height: '500px',
          maxWidth: '90vw',
          maxHeight: '90vw',
        }}
      />
      {/* </div> */}

      {!isMobile && (
        // disable modal on mobile since point location can't be clicked to open the modal
        <CityModal
          key={modalOpen ? selectedCity : undefined}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          cityURI={selectedCity || ''}
          cityName={selectedCityName}
          cityStats={cityStats}
        />
      )}
    </div>
  );
};
